import { useState, useEffect, useRef, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { aboutData } from '@/mocks/about';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);
  const contactRef = useRef<HTMLElement>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      if (formRef.current) formRef.current.reset();
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          body.append(key, value.toString());
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9qmcuvnahb7382ovc6g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* keep raw */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        if (formRef.current) formRef.current.reset();
      } else {
        const serverMsg = (parsed?.meta as Record<string, unknown>)?.message as string
          || (parsed?.meta as Record<string, unknown>)?.detail as string
          || (parsed as Record<string, unknown>)?.message as string
          || responseText;
        const isSpam = typeof serverMsg === 'string' && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'));
        if (isSpam) {
          setFormError('提交失败，请稍后重试。');
        } else {
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试。');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试。');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于我们 - 俄罗斯妈妈',
          description: '了解俄罗斯妈妈的品牌故事、核心价值与联系方式',
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 md:pt-24">
          <div className="max-w-5xl mx-auto w-full px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>

            {/* Hero */}
            <section className="text-center py-10 md:py-16">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 mb-4">
                关于「{aboutData.brandName}」
              </h1>
              <p className="text-lg md:text-xl text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                {aboutData.tagline}
              </p>
            </section>

            {/* Brand Story */}
            <section className="py-10 md:py-14">
              <div className="max-w-3xl mx-auto">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                  我们的故事
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  {aboutData.story}
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {aboutData.mission}
                </p>
              </div>
            </section>

            {/* Stats */}
            <section className="py-10 md:py-14">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {aboutData.stats.map((stat) => (
                  <div key={stat.label} className="text-center p-6 bg-background-100 rounded-lg">
                    <p className="text-3xl md:text-4xl font-heading font-bold text-primary-500 mb-1">
                      {stat.value}
                    </p>
                    <p className="text-xs md:text-sm text-foreground-400">{stat.label}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Values */}
            <section className="py-10 md:py-14">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
                核心价值
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {aboutData.values.map((val) => (
                  <div key={val.title} className="p-6 bg-background-100 rounded-lg flex gap-4">
                    <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-50 flex-shrink-0">
                      <i className={`${val.icon} text-primary-600 text-xl`}></i>
                    </span>
                    <div>
                      <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">
                        {val.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">{val.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact */}
            <section ref={contactRef} id="contact" className="py-10 md:py-14 mb-14">
              <div className="max-w-2xl mx-auto">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
                  联系我们
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
                  <div className="text-center p-5 bg-background-100 rounded-lg">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-50 mx-auto mb-3">
                      <i className="ri-mail-line text-accent-600 text-lg"></i>
                    </span>
                    <p className="text-xs text-foreground-400 mb-1">邮箱</p>
                    <p className="text-sm text-foreground-700 font-medium">{aboutData.contact.email}</p>
                  </div>
                  <div className="text-center p-5 bg-background-100 rounded-lg">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-50 mx-auto mb-3">
                      <i className="ri-wechat-line text-secondary-600 text-lg"></i>
                    </span>
                    <p className="text-xs text-foreground-400 mb-1">微信</p>
                    <p className="text-sm text-foreground-700 font-medium">{aboutData.contact.wechat}</p>
                  </div>
                  <div className="text-center p-5 bg-background-100 rounded-lg">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 mx-auto mb-3">
                      <i className="ri-map-pin-line text-primary-600 text-lg"></i>
                    </span>
                    <p className="text-xs text-foreground-400 mb-1">地址</p>
                    <p className="text-sm text-foreground-700 font-medium">{aboutData.contact.address}</p>
                  </div>
                </div>

                {/* Contact Form */}
                <form ref={formRef} onSubmit={handleSubmit} data-readdy-form="" className="bg-background-100 rounded-lg p-6 md:p-8">
                  <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-6">发送消息</h3>

                  {formStatus === 'success' && (
                    <div className="mb-6 p-4 rounded-lg bg-secondary-50 text-secondary-700 text-sm">
                      消息已发送！我们会尽快回复您。
                    </div>
                  )}

                  {formStatus === 'error' && (
                    <div className="mb-6 p-4 rounded-lg bg-primary-50 text-primary-700 text-sm">
                      {formError}
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名 *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                        placeholder="您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱 *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="消息主题"
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">消息 *</label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                      placeholder="请输入您的消息（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>

                  <input
                    type="text"
                    name="phone_alt"
                    className="honeypot-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {formStatus === 'loading' ? '发送中...' : '发送消息'}
                  </button>
                </form>
              </div>
            </section>
          </div>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}